import type { PhotoPresentationLayout } from '@shared/utils/photoPresentationLayout';

/**
 * Static `sizes` expressions for the eager-SSR sharp hero layer, per header layout.
 *
 * SSR cannot measure containers (`useSizeObserver` is 0 server-side), so each real-<img> layout
 * states its known CSS geometry, derived by reading each layout's actual rendering CSS/JS
 * (react-slick config, grid templates, aspect-ratio math) — not measured empirically. Derivation
 * anchors on HeaderPhoto.tsx's FULL_WIDTH_LAYOUTS split (viewport-width vs page-column) with a
 * per-photo divisor for multi-photo layouts.
 *
 * Over-estimate-only rule: every value here must ROUND UP. Post-hydration, the measured
 * (DPR-capped) sizes takes over and browsers never downgrade an already-loaded candidate, so an
 * over-estimate costs at most one wasted srcset bucket — but an under-estimate re-fires LCP (see
 * cropScaledStaticSizes's comment in OptimizedEventGuestSitePhoto.tsx for why that's expensive).
 *
 * CSS-background layouts (fullWidth, center, pageWidth, photoMarquee*) are deliberately absent:
 * srcset/sizes cannot apply to background-image, and they're out of scope for this map.
 */
/**
 * Brannan's inner frame is the bounding box for every non-viewport-level header — measured
 * 1024px wide on desktop (page-width layouts render inside it, see the AnimatedBorder frame).
 * Layouts in HeaderPhoto.tsx's FULL_WIDTH_LAYOUTS break out of the frame to the full viewport;
 * everything else is capped by it, so their desktop sizes term is a px cap, not a vw fraction.
 * Below this breakpoint the frame is fluid (ColumnsV2 `fluid` — see Frames.tsx), i.e. effectively
 * full viewport width minus a small gutter, so the safe estimate there is a plain `100vw`.
 * (Most header layouts are Brannan-only — Aloha offers just center + photoSlider, see
 * PHOTO_PRESENTATION_LAYOUT_SCHEMA_URL_BY_WEBSITE_LAYOUT.)
 */
const BRANNAN_INNER_FRAME_MAX_PX = 1024;

// PhotoArch (widgets/Custom/components/PhotoArch/{PhotoArch,ArchPhoto}.tsx): the container caps at
// PHOTO_ARCH_MAX_HEIGHT_PX tall, and the <img> renders at `height * PHOTO_ARCH_ASPECT_RATIO` wide —
// so once the frame is wide enough to hit that height cap, the rendered width stops growing at
// this product, regardless of how much wider the frame gets.
const PHOTO_ARCH_MAX_HEIGHT_PX = 600;
const PHOTO_ARCH_ASPECT_RATIO = 9 / 7;

// PhotoTrio's grid (widgets/Custom/components/PhotoTrio/PhotoTrio.tsx) switches 1→3 columns at
// joykit's `xs` breakpoint alias, which is a fixed 480px regardless of any per-theme
// `breakpointWidths` override (see joykit's breakpointAliasPx) — not the 768px `sm2` alias.
const PHOTO_TRIO_GRID_BREAKPOINT_PX = 480;

export const SSR_HERO_SIZES: Partial<Record<PhotoPresentationLayout, string>> = {
  // Viewport-level layouts — full-bleed at every breakpoint. Only takes effect when the header
  // photo has cropInfo: PhotoBackgroundRenderer then renders a real fixed-aspect <img> eagerly;
  // without cropInfo it falls back to a viewport-sized CSS-background div instead, which stays on
  // the legacy hydration-gated path regardless of this entry.
  photoBackground: '100vw',
  photoBackgroundMonogram: '100vw',
  // FULL_WIDTH_LAYOUTS (break out of the inner frame): fraction of the viewport on desktop.
  // Duo (PhotoDuo.tsx): the 2/3-primary / 1/3-secondary grid holds at every breakpoint — it never
  // stacks to full-width on mobile. `67vw` is the primary photo's share rounded up; used as a safe
  // blanket for both slots here (DuoPhotoRenderer derives each photo's own tighter estimate from
  // its actual grid partition, so this constant is only the eager-SSR-gate/existence fallback).
  photoDuoLeft: '67vw',
  photoDuoRight: '67vw',
  // Below PHOTO_TRIO_GRID_BREAKPOINT_PX the grid is a single column (100vw); at/above it, 3 equal
  // columns (100/3 ≈ 33.33vw, rounded up).
  photoTrio: `(min-width: ${PHOTO_TRIO_GRID_BREAKPOINT_PX}px) 34vw, 100vw`,
  // Slider (BrannanPhotoSlider.tsx via react-slick: slidesToShow: 1, fade: true): exactly one full
  // slide is visible at a time, sized to its 100vw container (FULL_WIDTH_PROPERTIES) — no peek.
  photoSlider: '100vw',
  // Page-column layouts (inside the 1024px inner frame): the frame, not the viewport, is the
  // bound once the viewport exceeds it — px caps derived from BRANNAN_INNER_FRAME_MAX_PX.
  // Same PhotoTrio component and the same {_: 1 col, xs: 3 cols} grid as photoTrio above, so the
  // 1→3 column switch is at PHOTO_TRIO_GRID_BREAKPOINT_PX here too — NOT at the frame's 768px.
  photoTrioPageWidth: `(min-width: 1024px) ${Math.round(BRANNAN_INNER_FRAME_MAX_PX / 3)}px, (min-width: ${PHOTO_TRIO_GRID_BREAKPOINT_PX}px) 34vw, 100vw`,
  // Same slider as photoSlider, but constrained to PAGE_WIDTH_PROPERTIES (100% of the inner frame,
  // not the viewport) — the frame is fluid below BRANNAN_INNER_FRAME_MAX_PX, so `100vw` is the safe
  // term there, and the frame's own px cap applies once the viewport exceeds it.
  photoSliderPageWidth: `(min-width: 1024px) ${BRANNAN_INNER_FRAME_MAX_PX}px, 100vw`,
  // photoArch is NOT in FULL_WIDTH_LAYOUTS — it renders inside the frame too. Below the frame's
  // px cap the rendered width just tracks the (fluid) frame width, so `100vw` safely bounds it;
  // once the frame is wide enough to hit PHOTO_ARCH_MAX_HEIGHT_PX, width is pinned at the product.
  photoArch: `(min-width: 1024px) ${Math.round(PHOTO_ARCH_MAX_HEIGHT_PX * PHOTO_ARCH_ASPECT_RATIO)}px, 100vw`,
};

/**
 * Looks up the SSR-safe `sizes` string for a header photo layout. Returns `undefined` for
 * layouts without an eager-SSR decision (CSS-background layouts, video, photo-less, or an
 * absent layout) — callers pass this straight through as `OptimizedEventGuestSitePhoto`'s
 * optional `ssrSizes` prop, which self-gates to the legacy path when undefined.
 */
export const getSsrHeroSizes = (layout: PhotoPresentationLayout | undefined): string | undefined => (layout ? SSR_HERO_SIZES[layout] : undefined);

/**
 * Multiplies every `<number>vw` / `<number>px` LENGTH term in a `sizes` expression by `factor`,
 * rounding to the nearest integer. A `sizes` entry is `[<media-condition>] <length>` — only the
 * trailing length token scales; a `px` value inside a media condition, e.g. `(min-width: 768px)`,
 * is deliberately left alone (splitting each comma-separated entry on whitespace and only ever
 * touching its last token keeps the two lexically identical `768px` and `100px` from being
 * conflated — a blanket "every px/vw in the string" replace would also rescale the breakpoint).
 *
 * Exists to crop-scale the eager-SSR sharp layer's static `sizes` estimate:
 * `OptimizedEventGuestSitePhoto`'s cropped branches render the `<img>` itself at
 * `container × 100/cropInfo.width` (e.g. 2.67x on a 37.5% mobile crop), so the per-layout
 * `SSR_HERO_SIZES` container-width guess understates the actually-rendered width by exactly that
 * factor unless it's scaled up here first.
 *
 * `factor <= 1.001` (no meaningful crop scale-up — 1.001 tolerates float noise from
 * `100 / cropInfo.width`) or non-finite returns `expr` unchanged, so uncropped/no-op callers pay
 * no parsing cost and get back the exact same string reference.
 */
export const scaleSizesExpression = (expr: string, factor: number): string => {
  if (!Number.isFinite(factor) || factor <= 1.001) return expr;

  const LENGTH_TERM = /^(-?\d+(?:\.\d+)?)(vw|px)$/;

  return expr
    .split(',')
    .map(entry => {
      const trimmed = entry.trim();
      const lastSpaceIndex = trimmed.lastIndexOf(' ');
      const lastToken = lastSpaceIndex === -1 ? trimmed : trimmed.slice(lastSpaceIndex + 1);
      const match = lastToken.match(LENGTH_TERM);
      if (!match) return entry; // no scalable length term (defensive — shouldn't happen for real sizes strings)

      const [, num, unit] = match;
      const scaledToken = `${Math.round(parseFloat(num) * factor)}${unit}`;
      const prefix = lastSpaceIndex === -1 ? '' : trimmed.slice(0, lastSpaceIndex + 1);
      // Preserve the entry's original leading whitespace (e.g. the space after a comma) so
      // re-joining with ',' reproduces the source's formatting exactly, factor === identity aside.
      const leadingWhitespace = entry.slice(0, entry.length - entry.trimStart().length);
      return `${leadingWhitespace}${prefix}${scaledToken}`;
    })
    .join(',');
};
